import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CheckoutAddressTypeEnum } from '@sola/schema/dotcom/checkout/contactInfo.ts';
import type { UniqueAddressMap } from '@sola/schema/dotcom/shared/address.ts';
import { AddressSelectOptions } from '@sola/schema/dotcom/shared/address.ts';
import { COUNTRY_CODE_US } from '@sola/schema/dotcom/shared/geo.ts';
import { LinkStyle } from '../../atoms/link-style/link-style.tsx';
import { cn } from '../../utils/helpers.ts';
import useCollapsible from '../../utils/hooks/useCollapsible.ts';
import { AddressFields } from '../address-fields/address-fields.tsx';
import { Fieldset } from '../fieldset/fieldset.tsx';
import type { SelectItemProps } from '../select/select.tsx';
import { Select } from '../select/select.tsx';
export type AddressSelectProps = {
  name: string;
  label: string;
  addresses?: UniqueAddressMap;
  defaultAddress?: string;
  addressAddType?: CheckoutAddressTypeEnum;
  isNewAddress?: boolean;
};
export const AddressSelect: React.FC<AddressSelectProps> = ({
  name,
  label,
  addresses,
  defaultAddress,
  addressAddType = CheckoutAddressTypeEnum.Billing,
  isNewAddress
}) => {
  const {
    t
  } = useTranslation(['core', 'payment-methods']);
  const {
    setValue,
    getValues
  } = useFormContext();
  const addressSelectItems: SelectItemProps[] = [];
  if (addresses?.size) {
    for (const [key, value] of addresses.entries()) {
      addressSelectItems.push({
        value: key,
        text: value.text
      });
    }
  }

  // TODO: Could make the "add address" optional with a prop like allowAddressAdd
  const addAddressSelectItem: SelectItemProps = {
    value: AddressSelectOptions.Add,
    text: t('payment-methods:addNewAddress')
  };
  addressSelectItems.push(addAddressSelectItem);
  const initialAddress = defaultAddress || addressSelectItems[0].value;
  const [selectedAddress, setSelectedAddress] = useState(initialAddress);
  const {
    isOpen: isAddingAddress,
    setIsOpen: setisAddingAddress,
    triggerProps: addressTriggerProps,
    targetProps: {
      ...addressTargetProps
    }
  } = useCollapsible({
    defaultExpanded: isNewAddress || !addresses?.size,
    enabled: true,
    forceMount: true,
    onCollapseChange: isOpen => {
      if (isOpen) {
        setValue(name, addAddressSelectItem.value);
        return;
      }
      setValue(name, selectedAddress || initialAddress);
    }
  });
  const {
    className: addressClasses,
    ...otherAddressTargetProps
  } = addressTargetProps;
  return <div className="w-full flex flex-col gap-comfy-lg">
      <Select name={name} label={label} defaultValue={initialAddress} onChange={e => {
      if (setisAddingAddress) {
        if (e.target.value === addAddressSelectItem.value) {
          setisAddingAddress(true);
          return;
        }
        setSelectedAddress(e.target.value);
        setisAddingAddress(false);
      }
    }} value={getValues(name)} items={addressSelectItems} shouldUnregister required />

      <LinkStyle iconBefore={isAddingAddress ? 'minus-circle' : 'plus'} noPadding noUnderline size="lg">
        <button type="button" {...addressTriggerProps}>
          {isAddingAddress ? t('core:actions.cancel') : t('payment-methods:addNewAddress')}
        </button>
      </LinkStyle>

      {isAddingAddress && <Fieldset className={cn('flex flex-col gap-comfy-sm', {
      [`${addressClasses}`]: !!addressClasses
    })} {...otherAddressTargetProps}>
          <AddressFields addressType={addressAddType} defaultCountry={COUNTRY_CODE_US} shouldUnregister />
        </Fieldset>}
    </div>;
};